/**
 * My Email Notifications — the category catalog.
 *
 * Single source of truth for the user-side "My Email Notifications" page (Tools) AND the
 * edge functions that serve it. Both import this file: the React page for tabs/copy, the
 * `notification-activity` function for the type allowlist and loan binding, and
 * `send-review-request` for resend dispatch.
 *
 * DELIBERATELY IMPORT-FREE so Vitest (Node) and the portal bundle can consume it from the
 * Deno tree. Keep it that way.
 *
 * Honest extensibility rule: adding a category is ONLY a catalog entry when (a) the producer
 * already writes `metadata.encompass_loan_id` on its `communications` rows (the only loan
 * identity supported today) and (b) resend uses an existing `resendKind`. Anything else is a
 * catalog entry plus the adapter that provides the missing capability.
 */

/** How a category's communications rows bind to a loan. Only one kind exists today. */
export type MyEmailNotificationLoanIdentity = 'encompass_loan_id'

/** Which sender knows how to resend a category's emails. `null` = read-only category. */
export type MyEmailNotificationResendKind = 'google_review_request'

export interface MyEmailNotificationCategory {
  /** Stable id sent by the client (`categoryId`); never shown to users. */
  id: string
  /** Tab label. */
  label: string
  /** `communications.metadata.notification_type` values grouped under this tab. */
  notificationTypes: readonly string[]
  loanIdentity: MyEmailNotificationLoanIdentity
  resendKind: MyEmailNotificationResendKind | null
  copy: {
    /** Shown when the user's loans have no emails of this category at all. */
    empty: string
    /** Second line of the inline resend confirmation. */
    confirm: string
  }
}

/** DB feature flag (global row + per-user overrides) gating the page, the `my_*` activity actions and loan-team resends. */
export const MY_EMAIL_NOTIFICATIONS_FLAG = 'my_email_notifications_v1'

export const MY_EMAIL_NOTIFICATION_CATEGORIES: readonly MyEmailNotificationCategory[] = [
  {
    id: 'google_reviews',
    label: 'Google Reviews',
    notificationTypes: ['google_review_request', 'google_review_request_realtor'],
    loanIdentity: 'encompass_loan_id',
    resendKind: 'google_review_request',
    copy: {
      empty: 'No Google review emails on your loans yet.',
      confirm: 'Sends the current Google review request to the same address. The loan officer stays copied.',
    },
  },
]

export function getMyEmailNotificationCategory(
  categoryId: string | null | undefined,
): MyEmailNotificationCategory | null {
  if (!categoryId) return null
  return MY_EMAIL_NOTIFICATION_CATEGORIES.find((category) => category.id === categoryId) ?? null
}

/** The category that owns a notification type, if any. */
export function categoryForNotificationType(
  notificationType: string | null | undefined,
): MyEmailNotificationCategory | null {
  if (!notificationType) return null
  return (
    MY_EMAIL_NOTIFICATION_CATEGORIES.find((category) =>
      category.notificationTypes.includes(notificationType),
    ) ?? null
  )
}

/** Which sender may resend this notification type; `null` when it cannot be resent. */
export function resendKindForNotificationType(
  notificationType: string | null | undefined,
): MyEmailNotificationResendKind | null {
  return categoryForNotificationType(notificationType)?.resendKind ?? null
}

/** Every notification type any category can resend (admin Activity uses the same list). */
export const RESENDABLE_NOTIFICATION_TYPES: readonly string[] = MY_EMAIL_NOTIFICATION_CATEGORIES
  .filter((category) => category.resendKind !== null)
  .flatMap((category) => category.notificationTypes)
